import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatTabsModule } from '@angular/material/tabs'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field'
import { MatInput, MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatNativeDateModule } from '@angular/material/core'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import { MatTooltipModule } from '@angular/material/tooltip'
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask'
import moment from 'moment/moment'
import { firstValueFrom } from 'rxjs'

import { SupplierPortalService } from '../../../core/services/supplier-portal/supplier-portal.service'
import { DocumentTypeService } from '../../../core/services/document/document-type/document-type.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { FornecedorDocumentoUpload, FornecedorMotoristaVm } from '../../../core/models/supplier-portal.model'
import { ErrorRequestModel } from '../../../core/models/response.model'
import { cpfValidator } from '../../../shared/utils/cpf_validator'
import { ModalDriverEditComponent } from '../modal-driver-edit/modal-driver-edit.component'
import { ModalSupplierPortalDocumentsComponent } from '../modal-supplier-portal-documents/modal-supplier-portal-documents.component'
import { DocumentTypeOption, loadAcceptedDocumentTypes } from '../supplier-portal-document-type.util'

interface PendingDocument {
  descricao: string
  tipoDocId: number
  tipoDocDescricao: string
  arquivo: File
}

export interface AssignDriverResult {
  success: boolean
  avisos: string[]
}

// Motorista é N:N — por isso essa modal tem 2 abas: "Motoristas" (meus + busca por CPF,
// pra achar/vincular um motorista já cadastrado em outro fornecedor, tudo no mesmo lugar)
// e "Cadastrar novo" (find-or-create no backend: se o CPF já existe, só vincula, não duplica).
@Component({
  selector: 'modal-assign-driver',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    MatFormField, MatInput, MatLabel,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinner,
    MatSlideToggle,
    MatTooltipModule,
    NgxMaskDirective,
  ],
  templateUrl: './modal-assign-driver.component.html',
  styleUrl: './modal-assign-driver.component.scss',
  providers: [provideNgxMask()],
})
export class ModalAssignDriverComponent implements OnInit {
  @Output() response = new EventEmitter<AssignDriverResult>()

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: { osId: string },
    private dialogRef: MatDialogRef<ModalAssignDriverComponent>,
    private dialog: MatDialog,
    private supplierPortalService: SupplierPortalService,
    private documentTypeService: DocumentTypeService,
    private snackbar: SnackbarCustomService,
  ) {}

  listCategory: any[] = [
    { id: 'A', description: 'A' },
    { id: 'B', description: 'B' },
    { id: 'C', description: 'C' },
    { id: 'D', description: 'D' },
    { id: 'E', description: 'E' },
  ]

  // Aba "Motoristas" — meus motoristas + busca por CPF, no mesmo lugar
  myDrivers: FornecedorMotoristaVm[] = []
  loadingList = false
  assigningId: number | null = null

  cpfSearch = ''
  searchingCpf = false
  cpfResult: FornecedorMotoristaVm | null = null
  cpfNotFound = false

  // Aba "Cadastrar novo"
  form!: FormGroup
  creating = false

  // Documentos anexados durante o cadastro — ainda não existe motoristaId, então ficam
  // "staged" localmente e só são enviados (em sequência) depois que o motorista é criado.
  tipoDocOptions: DocumentTypeOption[] = []
  pendingDocuments: PendingDocument[] = []
  pendingDescricao = ''
  pendingTipoDocId: number | null = null
  pendingFile: File | null = null

  ngOnInit(): void {
    this.createForm()
    this.loadMyDrivers()
    this.loadDocumentTypes()
  }

  private createForm(): void {
    this.form = this.fb.group({
      nome: ['', Validators.required],
      cpf: ['', [Validators.required, cpfValidator()]],
      contato: ['', Validators.required],
      categoria: [''],
      numeroRegistro: [''],
      validade: ['', Validators.required], // obrigatória — regra nova do backend
      antecedentesCriminais: [false],
      antecedentesValidade: [''],
    })
  }

  private loadDocumentTypes(): void {
    loadAcceptedDocumentTypes(this.documentTypeService, 'motorista').subscribe({
      next: (options) => { this.tipoDocOptions = options },
      error: () => { this.tipoDocOptions = [] },
    })
  }

  onPendingFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    this.pendingFile = input.files && input.files.length > 0 ? input.files[0] : null
  }

  addPendingDocument(): void {
    if (!this.pendingDescricao || !this.pendingTipoDocId || !this.pendingFile) return
    const tipo = this.tipoDocOptions.find((t) => t.id === this.pendingTipoDocId)
    this.pendingDocuments.push({
      descricao: this.pendingDescricao,
      tipoDocId: this.pendingTipoDocId,
      tipoDocDescricao: tipo?.descricao ?? '',
      arquivo: this.pendingFile,
    })
    this.pendingDescricao = ''
    this.pendingTipoDocId = null
    this.pendingFile = null
  }

  removePendingDocument(index: number): void {
    this.pendingDocuments.splice(index, 1)
  }

  // Sequência pedida pelo backend: cria o motorista, pega o id da resposta, só então
  // envia os documentos um a um — upload de documento depende do id não existir antes.
  private async uploadPendingDocuments(motoristaId: number): Promise<void> {
    for (const doc of this.pendingDocuments) {
      const upload = new FornecedorDocumentoUpload()
      upload.Descricao = doc.descricao
      upload.TipoDocId = doc.tipoDocId
      upload.Arquivo = doc.arquivo
      try {
        await firstValueFrom(this.supplierPortalService.uploadMotoristaDocumento(motoristaId, upload.toFormData()))
      } catch {
        this.snackbar.warning(`Motorista cadastrado, mas falha ao enviar o documento "${doc.descricao}". Anexe novamente pela edição.`)
      }
    }
  }

  loadMyDrivers(): void {
    this.loadingList = true
    this.supplierPortalService.listPagedMotoristas(1, 50).subscribe({
      next: (res: any) => { this.myDrivers = res.data ?? [] },
      error: (err: ErrorRequestModel) => { this.loadingList = false; this.snackbar.errorRequest(err) },
      complete: () => { this.loadingList = false },
    })
  }

  driverBlocked(driver: FornecedorMotoristaVm): boolean {
    return !!driver.cnhVencida
  }

  assignExisting(motoristaId: number): void {
    this.assigningId = motoristaId
    this.supplierPortalService.atribuirMotorista(this.data.osId, motoristaId).subscribe({
      next: (res: any) => {
        this.snackbar.success(res.message)
        this.response.emit({ success: true, avisos: res.data?.avisos ?? [] })
        this.dialogRef.close()
      },
      error: (err: ErrorRequestModel) => { this.assigningId = null; this.snackbar.errorRequest(err) },
    })
  }

  searchByCpf(): void {
    const cpf = this.cpfSearch.replace(/\D/g, '')
    if (cpf.length === 0) return
    this.searchingCpf = true
    this.cpfResult = null
    this.cpfNotFound = false
    this.supplierPortalService.getMotoristaByCpf(cpf).subscribe({
      next: (res: any) => { this.cpfResult = res.data },
      error: () => {
        this.cpfNotFound = true
        this.form.get('cpf')?.setValue(this.cpfSearch)
      },
      complete: () => { this.searchingCpf = false },
    })
  }

  clearCpfSearch(): void {
    this.cpfSearch = ''
    this.cpfResult = null
    this.cpfNotFound = false
  }

  onCpfInputChange(): void {
    if (!this.cpfSearch) {
      this.cpfResult = null
      this.cpfNotFound = false
    }
  }

  vincularEAtribuir(): void {
    if (!this.cpfResult) return
    this.assigningId = this.cpfResult.id
    this.supplierPortalService.vincularMotorista(this.cpfResult.id).subscribe({
      next: () => { this.assignExisting(this.cpfResult!.id) },
      error: (err: ErrorRequestModel) => { this.assigningId = null; this.snackbar.errorRequest(err) },
    })
  }

  createAndAssign(): void {
    if (this.form.invalid) return
    this.creating = true
    const v = this.form.value
    const model = {
      nome: v.nome,
      cpf: (v.cpf || '').replace(/\D/g, ''),
      contato: v.contato,
      tipoContatoId: 4, // Celular — único tipo disponível hoje no portal
      numeroRegistro: v.numeroRegistro || '',
      categoria: v.categoria || '',
      validade: v.validade ? moment(v.validade).format('DD/MM/YYYY') : '',
      antecedentesCriminais: v.antecedentesCriminais ? 'S' : 'N',
      antecedentesValidade: v.antecedentesValidade ? moment(v.antecedentesValidade).format('DD/MM/YYYY') : '',
    }
    this.supplierPortalService.createMotorista(model).subscribe({
      next: (res: any) => {
        const motoristaId = res.data?.id
        if (motoristaId) {
          // Sequência: cria → pega o id da resposta → sobe os documentos pendentes → atribui.
          this.uploadPendingDocuments(motoristaId).then(() => {
            this.creating = false
            this.assignExisting(motoristaId)
          })
        } else {
          this.creating = false
          this.snackbar.success(res.message)
          this.response.emit({ success: true, avisos: [] })
          this.dialogRef.close()
        }
      },
      error: (err: ErrorRequestModel) => { this.creating = false; this.snackbar.errorRequest(err) },
    })
  }

  editDriver(driver: FornecedorMotoristaVm): void {
    const dialogRef = this.dialog.open(ModalDriverEditComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '1000px',
      maxWidth: '95vw',
      data: { motorista: driver },
    })
    dialogRef.componentInstance.response.subscribe((success: boolean) => {
      if (success) this.loadMyDrivers()
    })
  }

  openDocuments(driver: FornecedorMotoristaVm): void {
    this.dialog.open(ModalSupplierPortalDocumentsComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      width: '800px',
      maxWidth: '800px',
      data: { escopo: 'motorista', ownerId: driver.id, titulo: driver.nome },
    })
  }

  close(): void { this.dialogRef.close() }
}
